import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import {
  AutoControlledComponent as Component,
  customPropTypes,
  getElementType,
  getUnhandledProps,
} from 'components/lib';

import TabPane from './TabPane';
import Menu from '../Menu';

import './Tab.scss';
/* eslint-disable react/prefer-stateless-function */
class Tab extends Component {
  static propTypes = {
    /** An element type to render as (string or function). */
    as: customPropTypes.as,

    /** The initial activeIndex. */
    defaultActiveIndex: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),

    /** Index of the currently active tab. */
    activeIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /**
     * Shorthand props for the Menu.
     * tabular, if true, will derive final value from `menuPosition`, otherwise set 'left' or 'right' explicitly.
     */
    menu: PropTypes.object,
    /**
     * Called on tab change.
     *
     * @param {SyntheticEvent} event - React's original SyntheticEvent.
     * @param {object} data - All props and proposed new activeIndex.
     * @param {object} data.activeIndex - The new proposed activeIndex.
     */
    onTabChange: PropTypes.func,

    /**
     * Array of objects describing each Menu.Item and Tab.Pane:
     * { menuItem: 'Home', render: () => <Tab.Pane /> }
     * or
     * { menuItem: 'Home', pane: 'Welcome' }
     */
    panes: PropTypes.arrayOf(
      PropTypes.shape({
        menuItem: customPropTypes.itemShorthand,
        pane: customPropTypes.itemShorthand,
        render: PropTypes.func,
      }),
    ),

    /** A Tab can render only active pane. */
    renderActiveOnly: PropTypes.bool,
  };

  static autoControlledProps = ['activeIndex'];

  static handledProps = [
    'activeIndex',
    'panes',
    'renderActiveOnly',
    'menu',
    'onTabChange',
  ];

  static defaultProps = {
    menu: {},
    renderActiveOnly: true,
  };

  static Pane = TabPane;

  getInitialAutoControlledState() {
    const { panes } = this.props;
    let activeIndex = 0;
    _.forEach(panes, (pane, index) => {
      if (
        _.get(pane, ['menuItem', 'props', 'children', 'props', 'active'], [])
      ) {
        activeIndex = index;
      }
    });
    return { activeIndex };
  }

  handleItemClick = (e, { index }) => {
    _.invoke(this.props, 'onTabChange', e, {
      ...this.props,
      activeIndex: index,
    });
    this.trySetState({ activeIndex: index });
  };

  renderItems() {
    const { panes, renderActiveOnly, className } = this.props;
    const { activeIndex } = this.state;

    if (renderActiveOnly)
      return _.invoke(_.get(panes, `[${activeIndex}]`), 'render', this.props);
    return _.map(panes, ({ pane }, index) =>
      TabPane.create(pane, {
        overrideProps: {
          active: index === activeIndex,
          className,
        },
      }),
    );
  }

  renderMenu() {
    const { menu, panes, className } = this.props;
    const { activeIndex } = this.state;
    return Menu.create(menu, {
      autoGenerateKey: false,
      overrideProps: {
        items: _.map(panes, 'menuItem'),
        onItemClick: this.handleItemClick,
        activeIndex,
        className,
      },
    });
  }

  render() {
    const rest = getUnhandledProps(Tab, this.props);
    const ElementType = getElementType(Tab, this.props);

    return (
      <ElementType {...rest}>
        {this.renderMenu()}
        {this.renderItems()}
      </ElementType>
    );
  }
}

export default Tab;
